<script setup lang="ts">
import AttendanceMissingModal from "./AttendanceMissingModal.vue";
import AttendanceBlockedCard from "./AttendanceBlockedCard.vue";
import AttendanceSheetViewer from "./AttendanceSheetViewer.vue";
import AttendanceSignatureTable from "./AttendanceSignatureTable.vue";
import AppButton from "~/components/Common/Buttons/AppButton.vue";
import LoadButton from "~/components/Common/Buttons/LoadButton.vue";
import FormField from "~/components/Common/Inputs/FormField.vue";
import SimpleDropdown from "~/components/Common/Dropdowns/SimpleDropdown.vue";
import SvgIcon from "~/components/Common/SvgIcon.vue";
import GlobalModal from "~/components/Common/Modals/GlobalModal.vue";
import HeaderDetails from "~/components/Header/HeaderDetails.vue";
import { useFormats } from "~/composables/utils/useFormat";
import { useAttendanceReviewStore } from "~/store/attendance/attendance-review.store";
import { attendanceTimeslotOptions } from "~/utils/helpers/attendance/timeslots";
import type { IAttendanceTimeslotOption } from "~/utils/interfaces/attendance-review-interfaces";

const store = useAttendanceReviewStore();
const formats = useFormats();

const isConfirming = ref(false);
const isSheetVisible = ref(true);

/**
 * The slots of a session split over several days or half-days. A confirmation ends exactly one of
 * them, and the sheet's hours are read against the whole session — so which one is a question only
 * the admin can answer, and the dropdown only appears when there is more than one answer to give.
 */
const timeslotOptions = computed(() =>
  attendanceTimeslotOptions(store.timeslots, formats.toDateDisplay, formats.toTimeDisplay),
);

const selectedTimeslot = computed(
  () => timeslotOptions.value.find((option) => option.timeslotId === store.timeslotId) ?? null,
);

function onPickTimeslot(option: IAttendanceTimeslotOption | null) {
  store.pickTimeslot(option?.timeslotId ?? null);
}

/**
 * "Confirmer" either writes the attendance or, when the sheet skipped booked collaborators, asks
 * about them first — the store owns that branch so the modal and the footer cannot disagree.
 */
async function onConfirm() {
  await store.requestConfirmation();
  isConfirming.value = false;
}
</script>

<template>
  <GlobalModal>
    <template #body>
      <div class="flex h-screen flex-col">
        <HeaderDetails back-icon="arrow_left" :close="false" back @back-click="store.close()">
          <template #page>Analyse feuille d'émargement</template>
        </HeaderDetails>

        <AttendanceBlockedCard v-if="store.blockedError" :error="store.blockedError" class="p-8" />
        <template v-else>
          <div class="flex min-h-0 flex-1">
            <!-- Hidden rather than unmounted: re-mounting re-downloads the PDF and resets its pager. -->
            <AttendanceSheetViewer v-show="isSheetVisible" :document="store.document" @close="isSheetVisible = false" />

            <!-- The folded panel: its toolbar strip, kept just wide enough for the icon to stay put. -->
            <div
              v-if="!isSheetVisible"
              class="flex shrink-0 flex-col border-r border-stroke-color bg-canvas-grey-color"
            >
              <div class="flex h-10 shrink-0 items-center border-b border-stroke-color bg-light-color px-4">
                <SvgIcon
                  name="document"
                  class="h-[16px] w-[16px] shrink-0 cursor-pointer text-secondary-color"
                  title="Afficher la feuille d'émargement"
                  @click="isSheetVisible = true"
                />
              </div>
            </div>

            <section class="flex min-w-0 flex-1 flex-col overflow-y-auto px-8 pt-6">
              <AttendanceSignatureTable />
            </section>
          </div>

          <footer
            class="flex shrink-0 items-end gap-4 border-t border-stroke-color px-8 py-4"
            :class="store.needsTimeslotChoice ? 'justify-between' : 'justify-end'"
          >
            <FormField
              v-if="store.needsTimeslotChoice"
              class="min-w-0 max-w-[420px] flex-1"
              label="Créneau à émarger"
              required
            >
              <!--
              Not flagged in red while it is simply unanswered: the field opens empty by design. The
              required mark and the disabled "Confirmer" are what say the answer is owed.
            -->
              <SimpleDropdown
                :model-value="selectedTimeslot"
                :options="timeslotOptions"
                label="label"
                track-by="timeslotId"
                placeholder="Sélectionner un créneau..."
                @update:model-value="onPickTimeslot"
              />
            </FormField>

            <div class="flex shrink-0 gap-4">
              <AppButton type="outlined" label="Retour" @click="store.close()" />
              <LoadButton
                v-model="isConfirming"
                class="button__default"
                text-confirm="Confirmer"
                :disabled="store.isTimeslotChoicePending"
                force-waiting
                @click="onConfirm()"
              />
            </div>
          </footer>
        </template>
      </div>
    </template>
  </GlobalModal>

  <AttendanceMissingModal v-if="store.isMissingModalOpen" />
</template>
